import React, { useState } from 'react';
import './ErrorPage.css';

const ErrorPage = ({ 
  error, 
  title = "Something went wrong", 
  message = "We encountered an unexpected error. Please try again.",
  onRetry,
  onGoHome 
}) => {
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const getErrorIcon = () => {
    if (error?.status === 404) return '🔍';
    if (error?.status === 500) return '⚙️';
    if (error?.status === 403) return '🚫';
    return '❌';
  };

  const getErrorTitle = () => {
    if (error?.status === 404) return "Page Not Found";
    if (error?.status === 500) return "Server Error";
    if (error?.status === 403) return "Access Denied";
    return title;
  };

  const getErrorMessage = () => {
    if (error?.status === 404) return "The page you're looking for doesn't exist.";
    if (error?.status === 500) return "Our servers encountered an error. Please try again later.";
    if (error?.status === 403) return "You don't have permission to access this resource.";
    return message;
  };

  return (
    <div className="error-page">
      <div className="error-container">
        <div className="error-icon">
          {getErrorIcon()}
        </div>
        
        <h1 className="error-title">
          {getErrorTitle()}
        </h1>
        
        <p className="error-message">
          {getErrorMessage()}
        </p>

        {error && (
          <div className="error-details">
            <details>
              <summary>Error Details</summary>
              <div className="error-code">
                <strong>Status:</strong> {error.status || 'Unknown'}
              </div>
              {error.message && (
                <div className="error-code">
                  <strong>Message:</strong> {error.message}
                </div>
              )}
              {error.details && (
                <div className="error-code">
                  <strong>Details:</strong> {error.details}
                </div>
              )}
            </details>
          </div>
        )}

        <div className="error-actions">
          {onRetry && (
            <button 
              className="error-button primary" 
              onClick={onRetry}
            >
              🔄 Try Again
            </button>
          )}
          
          {onGoHome && (
            <button 
              className="error-button secondary" 
              onClick={onGoHome}
            >
              🏠 Go to Dashboard
            </button>
          )}
        </div>

        <div className="error-help">
          <p>Need help? Contact support or check our documentation.</p>
          {!emailSent && (
            <button
              className="error-button secondary"
              onClick={async () => {
                setSendingEmail(true);
                try {
                  const errorReport = {
                    message: error?.message || 'Unknown error',
                    details: error?.details || error?.componentStack || 'No additional details',
                    stack: error?.stack || '',
                    userAgent: navigator.userAgent,
                    url: window.location.href,
                    timestamp: new Date().toISOString(),
                  };

                  // Send error report to backend
                  const response = await fetch('/api/errors/report', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      error: errorReport,
                      recipient: 'admin@stratovai.com',
                    }),
                  });

                  if (response.ok) {
                    setEmailSent(true);
                  } else {
                    // Fallback: open mailto link
                    const subject = encodeURIComponent(`Error Report: ${errorReport.message}`);
                    const body = encodeURIComponent(
                      `Error Details:\n\n${JSON.stringify(errorReport, null, 2)}`
                    );
                    window.location.href = `mailto:admin@stratovai.com?subject=${subject}&body=${body}`;
                    setEmailSent(true);
                  }
                } catch (err) {
                  // Fallback: open mailto link
                  const subject = encodeURIComponent(`Error Report: ${error?.message || 'Unknown error'}`);
                  const body = encodeURIComponent(
                    `Error Details:\n\nMessage: ${error?.message || 'Unknown'}\nDetails: ${error?.details || 'No details'}\nURL: ${window.location.href}\nTimestamp: ${new Date().toISOString()}`
                  );
                  window.location.href = `mailto:admin@stratovai.com?subject=${subject}&body=${body}`;
                  setEmailSent(true);
                } finally {
                  setSendingEmail(false);
                }
              }}
              disabled={sendingEmail}
              style={{ marginTop: '1rem' }}
            >
              {sendingEmail ? 'Sending...' : '📧 Send Error Report to Support'}
            </button>
          )}
          {emailSent && (
            <p style={{ marginTop: '1rem', color: '#4caf50', fontWeight: 500 }}>
              ✓ Error report sent to admin@stratovai.com
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorPage; 